/**
 * Colyseus connection seam — the one place the client talks to the server.
 *
 * Holds a single `colyseus.js` `Client` (the server endpoint from
 * `VITE_SERVER_URL`) and the player's currently-joined lobby + game rooms, so
 * scenes read server-synced state instead of local mocks. Scenes never decide
 * room outcomes; they `send(...)` requests and render the synced `RoomState`
 * (MASTER_PROMPT §4 rule 3).
 */

import { Client, type Room } from 'colyseus.js';
import {
  GAME_ROOM_NAME,
  LOBBY_ROOM_NAME,
  type CostumeId,
} from '@ponponland/shared';
import type { RoomSchema } from './roomSchema.js';

/**
 * Resolve the Colyseus endpoint.
 *
 * `VITE_SERVER_URL` is baked in at build time (CI passes the Render `wss://`
 * URL). When it is missing we fall back to the local dev server. Two guards
 * turn a misconfigured deploy into a *loud* failure instead of a silently-dead
 * UI (the create/join buttons just no-op when the lobby never connects):
 *   - mixed-content: a `ws://` endpoint on an `https://` page is blocked by the
 *     browser, so upgrade it to `wss://`.
 *   - prod-misconfig: a localhost endpoint in a production build means the
 *     `VITE_SERVER_URL` secret was not set — log a clear, actionable error.
 */
function resolveServerUrl(): string {
  const configured = (import.meta.env.VITE_SERVER_URL as string | undefined)?.trim();
  let url = configured && configured.length > 0 ? configured : 'ws://localhost:2567';

  const onHttps = typeof window !== 'undefined' && window.location?.protocol === 'https:';
  if (onHttps && url.startsWith('ws://')) {
    url = `wss://${url.slice('ws://'.length)}`;
  }

  if (import.meta.env.PROD && /\/\/(localhost|127\.0\.0\.1)\b/.test(url)) {
    console.error(
      `[ponponland] This production build is pointing at "${url}" and cannot reach the ` +
        'game server — VITE_SERVER_URL was not set at build time. Set the VITE_SERVER_URL ' +
        'repo secret to the wss:// server URL and redeploy the client.'
    );
  }
  return url;
}

const SERVER_URL = resolveServerUrl();

/** Identity sent when joining a game room (from the Splash profile). */
export interface JoinIdentity {
  name: string;
  costume: CostumeId;
}

let client: Client | null = null;

function getClient(): Client {
  if (!client) client = new Client(SERVER_URL);
  return client;
}

/** The live connections for the current tab. One lobby + at most one game room. */
export const net: {
  lobby: Room | null;
  game: Room<RoomSchema> | null;
} = {
  lobby: null,
  game: null,
};

/** Join (or reuse) the matchmaking LobbyRoom — the Room Browser's source. */
export async function joinLobby(): Promise<Room> {
  if (net.lobby) return net.lobby;
  // joinOrCreate: the first client spins the singleton lobby; the rest reuse it.
  net.lobby = await getClient().joinOrCreate(LOBBY_ROOM_NAME);
  return net.lobby;
}

/** Leave the LobbyRoom (e.g. once inside a game room, to stop list churn). */
export async function leaveLobby(): Promise<void> {
  const room = net.lobby;
  net.lobby = null;
  if (room) await room.leave();
}

/** Re-join a `GameRoom` by its 4-digit code (the room's `roomId`). */
export async function joinGameByCode(
  code: string,
  identity: JoinIdentity,
  password?: string
): Promise<Room<RoomSchema>> {
  const room = await getClient().joinById<RoomSchema>(code, {
    name: identity.name,
    costume: identity.costume,
    ...(password ? { password } : {}),
  });
  net.game = room;
  return room;
}

/** Leave the current game room (Back / on disconnect). */
export async function leaveGame(): Promise<void> {
  const room = net.game;
  net.game = null;
  if (room) await room.leave();
}

export { GAME_ROOM_NAME, LOBBY_ROOM_NAME };
